import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Save,
  FileText,
  Trash2,
  Edit,
  X,
  Search,
  User,
  Download,
  Send,
  Link as LinkIcon,
  Copy,
  Ban,
  Sparkles,
  Languages,
  Maximize2,
  Minimize2
} from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { getAssetUrl } from '../../utils/assetUrl';
import AdminContratoLinksPanel from './AdminContratoLinksPanel';

interface ContratoData {
  _id?: string;
  nome: string;
  sobrenome: string;
  dataNascimento: string;
  nacionalidade: string;
  documentoTipo?: 'passaporte' | 'cpf' | 'nif';
  passaporteNumero: string;
  passaporteValidade: string;
  email?: string;
  telefone?: string;
  endereco: {
    logradouro: string;
    numero: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    cep: string;
    pais: string;
  };
  dataContrato: string;
  localAssinatura: string;
  idioma?: 'pt' | 'en' | 'es';
  valorHonorarios?: number;
  formaPagamento?: 'avista' | 'parcelado_2' | 'parcelado_3';
  tipoPagamento?: string;
  orcamentoId?: string;
  createdAt?: string;
  status?: 'draft' | 'sent_for_signature' | 'signed' | 'expired' | 'revoked';
  signature?: {
    token?: string;
    expiresAt?: string;
    signedAt?: string;
  };
  signUrl?: string;
}

const FORMAS_PAGAMENTO = [
  { value: 'avista', label: 'À vista (integral na assinatura)' },
  { value: 'parcelado_2', label: 'Parcelado em 2 vezes (1ª na assinatura + 1 em 30 dias)' },
  { value: 'parcelado_3', label: 'Parcelado em 3 vezes (1ª na assinatura + 2 em 30 e 60 dias)' }
] as const;

const labelFormaPagamento = (forma?: string) =>
  FORMAS_PAGAMENTO.find(f => f.value === forma)?.label || forma || '—';

type TemplateLang = 'pt' | 'en' | 'es';

interface ContratoTemplateInfo {
  lang: TemplateLang;
  status: 'not_generated' | 'default' | 'draft' | 'active';
  generatedAt: string | null;
  activatedAt: string | null;
}

const TEMPLATE_LANG_LABEL: Record<TemplateLang, string> = { pt: 'Português', en: 'Inglês', es: 'Espanhol' };
const TEMPLATE_STATUS_LABEL: Record<ContratoTemplateInfo['status'], string> = {
  not_generated: 'Não gerado',
  default: 'Texto padrão do sistema',
  draft: 'Rascunho',
  active: 'Ativo'
};

interface TemplateSegment {
  kind: 'title' | 'clause' | 'item';
  label: string;
  text: string;
}

interface TemplateEditState {
  doc: Document;
  setters: Array<(text: string) => void>;
  originalPlaceholderCount: number;
}

function countPlaceholders(html: string): number {
  return (html.match(/\{\{[^}]*\}\}/g) || []).length;
}

// Extrai da doc parseada (em ordem) apenas o texto revisável: títulos de seção,
// cláusulas numeradas (pulando cláusulas 100% dinâmicas, ex.: {{honorariosClausula31}},
// que não têm prosa nenhuma para revisar) e itens de lista. Tudo o mais (cabeçalho
// repetido, blocos "ENTRE:"/"CONTRATADO:", bloco de assinatura) fica de fora — não é
// texto gerado por IA que precise de revisão, e assim a estrutura nunca é tocada.
function extractTemplateSegments(doc: Document): { segments: TemplateSegment[]; setters: Array<(text: string) => void> } {
  const segments: TemplateSegment[] = [];
  const setters: Array<(text: string) => void> = [];

  doc.body.querySelectorAll('p, li').forEach((el) => {
    if (el.tagName === 'LI') {
      const text = el.textContent?.trim() || '';
      if (!text) return;
      segments.push({ kind: 'item', label: 'Item da lista', text });
      setters.push((next) => { el.textContent = next; });
      return;
    }

    if (el.classList.contains('clause-title')) {
      const text = el.textContent?.trim() || '';
      segments.push({ kind: 'title', label: 'Título da seção', text });
      setters.push((next) => { el.textContent = next; });
      return;
    }

    const numSpan = el.querySelector('span.clause-num');
    if (numSpan) {
      const clauseLabel = numSpan.textContent?.trim() || '';
      const fullText = el.textContent?.trim() || '';
      const bodyText = fullText.startsWith(clauseLabel) ? fullText.slice(clauseLabel.length).trim() : fullText;
      if (/^\{\{[^}]*\}\}$/.test(bodyText)) return;
      segments.push({ kind: 'clause', label: `Cláusula ${clauseLabel}`, text: bodyText });
      setters.push((next) => {
        Array.from(el.childNodes).forEach((child) => {
          if (child !== numSpan) el.removeChild(child);
        });
        el.appendChild(doc.createTextNode(` ${next}`));
      });
    }
  });

  return { segments, setters };
}

interface AdvogadoOption {
  _id: string;
  nome: string;
  oabNumero: string;
  oabEstado: string;
  cedulaProfissional: string;
  logo?: string;
}

const emptyForm = (): ContratoData => ({
  nome: '',
  sobrenome: '',
  dataNascimento: '',
  nacionalidade: 'brasileira',
  documentoTipo: 'passaporte',
  passaporteNumero: '',
  passaporteValidade: '',
  email: '',
  telefone: '',
  endereco: {
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    cep: '',
    pais: 'Portugal'
  },
  dataContrato: new Date().toISOString().split('T')[0],
  localAssinatura: 'Vagos',
  idioma: 'pt',
  valorHonorarios: 600,
  formaPagamento: 'parcelado_3',
  tipoPagamento: ''
});

interface PrefillContrato {
  valorHonorarios?: number;
  formaPagamento?: string;
  orcamentoId?: string;
  clienteId?: string;
  nomeCliente?: string;
}

const AdminContrato: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [contratos, setContratos] = useState<ContratoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ContratoData>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [showAdvogadoModal, setShowAdvogadoModal] = useState(false);
  const [pendingContratoId, setPendingContratoId] = useState<string | null>(null);
  const [advogadosList, setAdvogadosList] = useState<AdvogadoOption[]>([]);
  const [selectedAdvogadoId, setSelectedAdvogadoId] = useState<string | null>(null);

  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureModalMode, setSignatureModalMode] = useState<'send' | 'link'>('send');
  const [signatureContratoId, setSignatureContratoId] = useState<string | null>(null);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [sendingSignature, setSendingSignature] = useState(false);
  const [signUrlPreview, setSignUrlPreview] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  const [templates, setTemplates] = useState<ContratoTemplateInfo[]>([]);
  const [generatingTemplateLang, setGeneratingTemplateLang] = useState<TemplateLang | null>(null);
  const [togglingTemplateLang, setTogglingTemplateLang] = useState<TemplateLang | null>(null);
  const [editingTemplateLang, setEditingTemplateLang] = useState<TemplateLang | null>(null);
  const [templateSegments, setTemplateSegments] = useState<TemplateSegment[]>([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const templateEditRef = useRef<TemplateEditState | null>(null);

  const { isExpanded: isAdvogadoModalExpanded, setIsExpanded: setIsAdvogadoModalExpanded, toggleExpanded: toggleAdvogadoModalExpanded } = useExpandable();
  useEscapeKey(() => setShowAdvogadoModal(false), showAdvogadoModal);

  const { isExpanded: isSignatureModalExpanded, setIsExpanded: setIsSignatureModalExpanded, toggleExpanded: toggleSignatureModalExpanded } = useExpandable();
  useEscapeKey(() => setShowSignatureModal(false), showSignatureModal);

  const { isExpanded: isTemplateModalExpanded, setIsExpanded: setIsTemplateModalExpanded, toggleExpanded: toggleTemplateModalExpanded } = useExpandable();
  useEscapeKey(() => { setEditingTemplateLang(null); templateEditRef.current = null; }, !!editingTemplateLang);

  const notifications = useNotifications();
  const confirm = useConfirm();

  useEffect(() => {
    fetchContratos();
  }, [searchTerm]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Chegou aqui a partir da conversão de um Orçamento — pré-preenche o formulário
  // com valor/forma de pagamento e mantém o vínculo (orcamentoId) para completar
  // os dados legais (passaporte, endereço) que o orçamento não coleta.
  useEffect(() => {
    const prefill = (location.state as { prefillContrato?: PrefillContrato } | null)?.prefillContrato;
    if (!prefill) return;

    const formaPagamentoValida = ['avista', 'parcelado_2', 'parcelado_3'].includes(prefill.formaPagamento || '')
      ? (prefill.formaPagamento as ContratoData['formaPagamento'])
      : 'parcelado_3';

    const [nome, ...resto] = (prefill.nomeCliente || '').trim().split(/\s+/);

    setEditingId(null);
    setFormData({
      ...emptyForm(),
      nome: nome || '',
      sobrenome: resto.join(' '),
      valorHonorarios: prefill.valorHonorarios ?? 600,
      formaPagamento: formaPagamentoValida,
      orcamentoId: prefill.orcamentoId
    });
    setShowForm(true);

    // Limpa o state da navegação para não reaplicar o prefill num refresh/voltar.
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get<ContratoTemplateInfo[]>('/contrato-templates');
      setTemplates(res.data);
    } catch (error) {
      console.error('Erro ao carregar modelos de contrato:', error);
    }
  };

  const handleGenerateTemplate = async (lang: TemplateLang) => {
    try {
      setGeneratingTemplateLang(lang);
      await axios.post(`/contrato-templates/${lang}/generate`);
      notifications.showSuccess('Rascunho gerado!', 'Revise o conteúdo antes de ativar.');
      fetchTemplates();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.showError('Erro ao gerar tradução', err.response?.data?.message || 'Tente novamente.');
    } finally {
      setGeneratingTemplateLang(null);
    }
  };

  const openEditTemplate = async (lang: TemplateLang) => {
    try {
      const res = await axios.get(`/contrato-templates/${lang}`);
      const html: string = res.data.html || '';
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const { segments, setters } = extractTemplateSegments(doc);

      templateEditRef.current = { doc, setters, originalPlaceholderCount: countPlaceholders(html) };
      setTemplateSegments(segments);
      setEditingTemplateLang(lang);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.showError('Erro ao carregar modelo', err.response?.data?.message || 'Tente novamente.');
    }
  };

  const updateTemplateSegment = (index: number, text: string) => {
    setTemplateSegments((prev) => prev.map((seg, i) => (i === index ? { ...seg, text } : seg)));
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplateLang || !templateEditRef.current) return;
    const { doc, setters, originalPlaceholderCount } = templateEditRef.current;

    templateSegments.forEach((seg, i) => setters[i]?.(seg.text));
    const html = `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;

    if (countPlaceholders(html) !== originalPlaceholderCount) {
      notifications.showError(
        'Não foi possível salvar',
        'A edição parece ter alterado um marcador {{...}} do modelo. Desfaça a alteração e tente novamente.'
      );
      return;
    }

    try {
      setSavingTemplate(true);
      await axios.put(`/contrato-templates/${editingTemplateLang}`, { html });
      notifications.success.settingsUpdated();
      setEditingTemplateLang(null);
      templateEditRef.current = null;
      fetchTemplates();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.showError('Erro ao salvar modelo', err.response?.data?.message || 'Tente novamente.');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleToggleTemplateActive = async (lang: TemplateLang, activate: boolean) => {
    try {
      setTogglingTemplateLang(lang);
      await axios.post(`/contrato-templates/${lang}/${activate ? 'activate' : 'deactivate'}`);
      notifications.success.settingsUpdated();
      fetchTemplates();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.showError('Erro', err.response?.data?.message || 'Tente novamente.');
    } finally {
      setTogglingTemplateLang(null);
    }
  };

  const fetchContratos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '50' });
      if (searchTerm) params.set('search', searchTerm);
      const res = await axios.get(`/contrato/list?${params}`);
      setContratos(res.data.contratos);
    } catch (error) {
      console.error(error);
      notifications.error.settingsUpdate('Erro ao carregar contratos');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm());
    setShowForm(true);
  };

  const openEdit = (c: ContratoData) => {
    setEditingId(c._id || null);
    setFormData({
      ...c,
      dataNascimento: c.dataNascimento.split('T')[0],
      documentoTipo: c.documentoTipo || 'passaporte',
      passaporteValidade: c.passaporteValidade ? c.passaporteValidade.split('T')[0] : '',
      dataContrato: c.dataContrato ? c.dataContrato.split('T')[0] : new Date().toISOString().split('T')[0],
      endereco: { ...c.endereco },
      valorHonorarios: c.valorHonorarios ?? 600,
      formaPagamento: c.formaPagamento || 'parcelado_3',
      tipoPagamento: c.tipoPagamento || ''
    });
    setShowForm(true);
  };

  const handleField = (field: string, value: string) => {
    if (field.startsWith('endereco.')) {
      const key = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        endereco: { ...prev.endereco, [key]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editingId) {
        await axios.put(`/contrato/${editingId}`, formData);
      } else {
        await axios.post('/contrato', formData);
      }
      notifications.success.settingsUpdated();
      setShowForm(false);
      fetchContratos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: 'Excluir contrato', message: 'Este registro será excluído permanentemente.', confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/contrato/${id}`);
      notifications.success.settingsUpdated();
      fetchContratos();
    } catch {
      notifications.error.settingsUpdate('Erro ao excluir');
    }
  };

  const openGeneratePdf = async (contratoId: string) => {
    try {
      const res = await axios.get<AdvogadoOption[]>('/advogado/all');
      if (res.data.length === 0) {
        notifications.error.settingsUpdate('Cadastre um advogado em Admin → Advogados');
        return;
      }
      setAdvogadosList(res.data);
      setSelectedAdvogadoId(res.data[0]._id);
      setPendingContratoId(contratoId);
      setShowAdvogadoModal(true);
    } catch {
      notifications.error.settingsUpdate('Erro ao carregar advogados');
    }
  };

  const generatePdf = async () => {
    if (!pendingContratoId || !selectedAdvogadoId) return;
    const contrato = contratos.find(c => c._id === pendingContratoId);
    try {
      setGeneratingId(pendingContratoId);
      setShowAdvogadoModal(false);
      const apiUrl = axios.defaults.baseURL || '/api';
      const query = new URLSearchParams({ advogadoId: selectedAdvogadoId });
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${apiUrl}/contrato/${pendingContratoId}/contrato-pdf?${query}`,
        { headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf' } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrato-${contrato?.nome || 'cliente'}-${contrato?.sobrenome || ''}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      notifications.success.settingsUpdated();
    } catch (error: unknown) {
      const err = error as Error;
      notifications.error.settingsUpdate('Erro ao gerar PDF: ' + err.message);
    } finally {
      setGeneratingId(null);
      setPendingContratoId(null);
    }
  };

  const openSendForSignature = (contratoId: string) => {
    setSignatureContratoId(contratoId);
    setSignatureModalMode('send');
    setExpiresInDays(30);
    setSignUrlPreview('');
    setLinkCopied(false);
    setShowSignatureModal(true);
  };

  const openViewSignatureLink = async (contratoId: string) => {
    try {
      const res = await axios.get(`/contrato/${contratoId}`);
      setSignatureContratoId(contratoId);
      setSignatureModalMode('link');
      setSignUrlPreview(res.data.signUrl || '');
      setLinkCopied(false);
      setShowSignatureModal(true);
    } catch {
      notifications.error.settingsUpdate('Erro ao carregar link de assinatura');
    }
  };

  const confirmSendForSignature = async () => {
    if (!signatureContratoId) return;
    try {
      setSendingSignature(true);
      const res = await axios.post(`/contrato/${signatureContratoId}/send-for-signature`, { expiresInDays });
      setSignUrlPreview(res.data.signUrl);
      setSignatureModalMode('link');
      notifications.success.settingsUpdated();
      fetchContratos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao enviar contrato para assinatura');
    } finally {
      setSendingSignature(false);
    }
  };

  const copySignUrl = () => {
    if (!signUrlPreview) return;
    navigator.clipboard.writeText(signUrlPreview);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const revokeSignatureLink = async () => {
    if (!signatureContratoId) return;
    if (!(await confirm({ title: 'Revogar link', message: 'O cliente não poderá mais assinar este contrato.', confirmText: 'Revogar', variant: 'danger' }))) return;
    try {
      await axios.post(`/contrato/${signatureContratoId}/revoke-signature-link`);
      notifications.success.settingsUpdated();
      setShowSignatureModal(false);
      fetchContratos();
    } catch {
      notifications.error.settingsUpdate('Erro ao revogar link');
    }
  };

  const downloadSignedPdf = async (contratoId: string) => {
    const contrato = contratos.find(c => c._id === contratoId);
    try {
      setGeneratingId(contratoId);
      const apiUrl = axios.defaults.baseURL || '/api';
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${apiUrl}/contrato/${contratoId}/contrato-pdf`,
        { headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf' } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrato-assinado-${contrato?.nome || 'cliente'}-${contrato?.sobrenome || ''}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      notifications.success.settingsUpdated();
    } catch (error: unknown) {
      const err = error as Error;
      notifications.error.settingsUpdate('Erro ao baixar contrato assinado: ' + err.message);
    } finally {
      setGeneratingId(null);
    }
  };

  const saveAndGenerate = async () => {
    try {
      setSaving(true);
      let id = editingId;
      if (editingId) {
        await axios.put(`/contrato/${editingId}`, formData);
      } else {
        const res = await axios.post('/contrato', formData);
        id = res.data.contrato._id;
      }
      setShowForm(false);
      await fetchContratos();
      if (id) await openGeneratePdf(id);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Breadcrumbs />
      </div>

      <AdminContratoLinksPanel />

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('admin.contract')}</h1>
          <p className="text-gray-600 mt-1 text-sm">
            Formulário administrativo para gerar o contrato de prestação de serviços com advogado selecionado.
          </p>
        </div>
        {!showForm && (
          <button
            onClick={openCreate}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Novo Cliente
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-1">
          <Languages className="w-5 h-5" /> Modelos de contrato / idiomas
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Edite as cláusulas em português a qualquer momento. Para inglês/espanhol, gere rascunhos com IA a partir do texto em português, revise o conteúdo e só depois ative — só idiomas ativos ficam disponíveis para escolher em um novo contrato.
        </p>
        <div className="space-y-3">
          {(['pt', 'en', 'es'] as TemplateLang[]).map((lang) => {
            const info = templates.find((tpl) => tpl.lang === lang);
            const status = info?.status || (lang === 'pt' ? 'default' : 'not_generated');
            return (
              <div key={lang} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-900">{TEMPLATE_LANG_LABEL[lang]}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    status === 'active' ? 'bg-green-100 text-green-700'
                      : status === 'draft' ? 'bg-yellow-100 text-yellow-700'
                      : status === 'default' ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {TEMPLATE_STATUS_LABEL[status]}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {lang !== 'pt' && (
                    <button
                      type="button"
                      onClick={() => handleGenerateTemplate(lang)}
                      disabled={generatingTemplateLang === lang}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
                    >
                      <Sparkles className="w-4 h-4" />
                      {generatingTemplateLang === lang ? 'Gerando...' : status === 'not_generated' ? 'Gerar com IA' : 'Regenerar com IA'}
                    </button>
                  )}
                  {(status !== 'not_generated' || lang === 'pt') && (
                    <button
                      type="button"
                      onClick={() => openEditTemplate(lang)}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                    >
                      Editar
                    </button>
                  )}
                  {status === 'draft' && (
                    <button
                      type="button"
                      onClick={() => handleToggleTemplateActive(lang, true)}
                      disabled={togglingTemplateLang === lang}
                      className="px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                    >
                      Ativar
                    </button>
                  )}
                  {status === 'active' && (
                    <button
                      type="button"
                      onClick={() => handleToggleTemplateActive(lang, false)}
                      disabled={togglingTemplateLang === lang}
                      className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors duration-200 disabled:opacity-50"
                    >
                      Desativar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
            <h2 className="text-xl font-semibold">
              {editingId ? 'Editar dados do contratante' : 'Novo contratante'}
            </h2>
            <button
              onClick={() => setShowForm(false)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              title="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" /> Dados do contratante
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input required value={formData.nome} onChange={e => handleField('nome', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sobrenome *</label>
                  <input required value={formData.sobrenome} onChange={e => handleField('sobrenome', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de nascimento *</label>
                  <input type="date" required value={formData.dataNascimento}
                    onChange={e => handleField('dataNascimento', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nacionalidade</label>
                  <input value={formData.nacionalidade} onChange={e => handleField('nacionalidade', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de documento *</label>
                  <select
                    required
                    value={formData.documentoTipo || 'passaporte'}
                    onChange={e => handleField('documentoTipo', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="passaporte">Passaporte</option>
                    <option value="cpf">CPF</option>
                    <option value="nif">NIF</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nº do documento {(formData.documentoTipo || 'passaporte') === 'passaporte' ? '(passaporte)' : formData.documentoTipo === 'cpf' ? '(CPF)' : '(NIF)'} *
                  </label>
                  <input required value={formData.passaporteNumero}
                    onChange={e => handleField('passaporteNumero', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                {(formData.documentoTipo || 'passaporte') === 'passaporte' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Validade passaporte *</label>
                    <input type="date" required value={formData.passaporteValidade}
                      onChange={e => handleField('passaporteValidade', e.target.value)}
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={formData.email || ''} onChange={e => handleField('email', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input value={formData.telefone || ''} onChange={e => handleField('telefone', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Domicílio (Portugal)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logradouro *</label>
                  <input required value={formData.endereco.logradouro}
                    onChange={e => handleField('endereco.logradouro', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número *</label>
                  <input required value={formData.endereco.numero}
                    onChange={e => handleField('endereco.numero', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                  <input value={formData.endereco.complemento || ''}
                    onChange={e => handleField('endereco.complemento', e.target.value)}
                    placeholder="Bloco, andar, etc."
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Freguesia *</label>
                  <input required value={formData.endereco.bairro}
                    onChange={e => handleField('endereco.bairro', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Concelho *</label>
                  <input required value={formData.endereco.cidade}
                    onChange={e => handleField('endereco.cidade', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código postal *</label>
                  <input required value={formData.endereco.cep}
                    onChange={e => handleField('endereco.cep', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">País *</label>
                  <input required value={formData.endereco.pais}
                    onChange={e => handleField('endereco.pais', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Honorários e pagamento</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor dos honorários (€) *</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    required
                    value={formData.valorHonorarios ?? 600}
                    onChange={e => setFormData(prev => ({
                      ...prev,
                      valorHonorarios: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forma de pagamento *</label>
                  <select
                    required
                    value={formData.formaPagamento || 'parcelado_3'}
                    onChange={e => handleField('formaPagamento', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    {FORMAS_PAGAMENTO.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo / detalhes de pagamento</label>
                  <input
                    type="text"
                    value={formData.tipoPagamento || ''}
                    onChange={e => handleField('tipoPagamento', e.target.value)}
                    placeholder="Ex.: Transferência bancária, MB Way..."
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Incluído na cláusula 3.1. O IBAN vem do cadastro do advogado ao gerar o PDF.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Assinatura do contrato</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Local *</label>
                  <input required value={formData.localAssinatura}
                    onChange={e => handleField('localAssinatura', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data do contrato *</label>
                  <input type="date" required value={formData.dataContrato}
                    onChange={e => handleField('dataContrato', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Idioma do contrato *</label>
                  <select
                    required
                    value={formData.idioma || 'pt'}
                    onChange={e => handleField('idioma', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pt">Português</option>
                    {(['en', 'es'] as TemplateLang[]).map((lang) => {
                      const isActive = templates.find((tpl) => tpl.lang === lang)?.status === 'active';
                      return (
                        <option key={lang} value={lang} disabled={!isActive}>
                          {TEMPLATE_LANG_LABEL[lang]}{!isActive ? ' (modelo não ativado)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200">
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Salvar</span>
                  </>
                )}
              </button>
              <button type="button" disabled={saving} onClick={saveAndGenerate}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Salvar e gerar PDF
              </button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <>
          <div className="mb-4 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome ou passaporte..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
            </div>
          ) : contratos.length === 0 ? (
            <p className="text-gray-500 text-center py-12">Nenhum registro. Clique em &quot;Novo Cliente&quot;.</p>
          ) : (
            <div className="space-y-4">
              {contratos.map(c => (
                <div key={c._id} className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900">{c.nome} {c.sobrenome}</p>
                      {c.status === 'signed' && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Assinado</span>
                      )}
                      {c.status === 'sent_for_signature' && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">Aguardando assinatura</span>
                      )}
                      {c.status === 'expired' && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Link expirado</span>
                      )}
                      {c.status === 'revoked' && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Link revogado</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {c.documentoTipo === 'cpf' ? 'CPF' : c.documentoTipo === 'nif' ? 'NIF' : 'Passaporte'}: {c.passaporteNumero}
                    </p>
                    <p className="text-xs text-gray-500">
                      Honorários: €{Number(c.valorHonorarios ?? 600).toFixed(2)} · {labelFormaPagamento(c.formaPagamento)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {c.endereco.cidade}, {c.endereco.pais}
                      {c.createdAt && ` · ${new Date(c.createdAt).toLocaleDateString('pt-PT')}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => openEdit(c)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200" title="Editar">
                      <Edit className="w-5 h-5" />
                    </button>
                    {c.status === 'signed' ? (
                      <button
                        onClick={() => c._id && downloadSignedPdf(c._id)}
                        disabled={generatingId === c._id}
                        className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors duration-200 disabled:opacity-50"
                        title="Baixar contrato assinado"
                      >
                        {generatingId === c._id ? (
                          <div className="w-5 h-5 rounded-full border-b-2 border-purple-600 animate-spin" />
                        ) : (
                          <Download className="w-5 h-5" />
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => c._id && openGeneratePdf(c._id)}
                        disabled={generatingId === c._id}
                        className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors duration-200 disabled:opacity-50"
                        title="Gerar contrato PDF"
                      >
                        {generatingId === c._id ? (
                          <div className="w-5 h-5 rounded-full border-b-2 border-purple-600 animate-spin" />
                        ) : (
                          <FileText className="w-5 h-5" />
                        )}
                      </button>
                    )}
                    {c.status === 'sent_for_signature' ? (
                      <button
                        onClick={() => c._id && openViewSignatureLink(c._id)}
                        className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors duration-200"
                        title="Ver link de assinatura"
                      >
                        <LinkIcon className="w-5 h-5" />
                      </button>
                    ) : c.status !== 'signed' ? (
                      <button
                        onClick={() => c._id && openSendForSignature(c._id)}
                        className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors duration-200"
                        title="Enviar para assinatura"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    ) : null}
                    <button onClick={() => c._id && handleDelete(c._id)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200" title="Excluir">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showAdvogadoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full overflow-y-auto p-6 transition-all duration-200 ${
            isAdvogadoModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-lg max-h-[90vh]'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Download className="w-5 h-5" /> Selecionar advogado
              </h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleAdvogadoModalExpanded}
                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title={isAdvogadoModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isAdvogadoModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => { setShowAdvogadoModal(false); setIsAdvogadoModalExpanded(false); }}
                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title="Fechar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="space-y-3 mb-6">
              {advogadosList.map(adv => (
                <label
                  key={adv._id}
                  className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer ${
                    selectedAdvogadoId === adv._id ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    checked={selectedAdvogadoId === adv._id}
                    onChange={() => setSelectedAdvogadoId(adv._id)}
                  />
                  {adv.logo ? (
                    <img src={getAssetUrl(adv.logo)} alt="" className="w-12 h-12 object-contain border rounded" />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                      <User className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{adv.nome}</p>
                    <p className="text-sm text-gray-600">OAB {adv.oabNumero}/{adv.oabEstado}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowAdvogadoModal(false); setIsAdvogadoModalExpanded(false); }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={generatePdf}
                disabled={!selectedAdvogadoId}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Gerar contrato PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {showSignatureModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full p-6 transition-all duration-200 ${
            isSignatureModalExpanded ? 'max-w-[95vw] max-h-[95vh] overflow-y-auto' : 'sm:max-w-lg max-h-[90vh] overflow-y-auto'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Send className="w-5 h-5" />
                {signatureModalMode === 'send' && !signUrlPreview ? 'Enviar para assinatura' : 'Link de assinatura'}
              </h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleSignatureModalExpanded}
                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title={isSignatureModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isSignatureModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => { setShowSignatureModal(false); setIsSignatureModalExpanded(false); }}
                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title="Fechar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {signatureModalMode === 'send' && !signUrlPreview ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Um link será enviado por e-mail ao cliente para revisar e assinar o contrato online, sem necessidade de conta.
                </p>
                <label className="block mb-4">
                  <span className="block text-sm font-medium text-gray-700 mb-1">Validade do link (dias)</span>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={expiresInDays}
                    onChange={e => setExpiresInDays(parseInt(e.target.value, 10) || 30)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </label>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => { setShowSignatureModal(false); setIsSignatureModalExpanded(false); }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmSendForSignature}
                    disabled={sendingSignature}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  >
                    {sendingSignature && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                    Enviar
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-2">Link de assinatura do cliente:</p>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    readOnly
                    value={signUrlPreview}
                    className="flex-1 px-3 py-2 border rounded-md bg-gray-50 text-sm"
                    onFocus={e => e.target.select()}
                  />
                  <button
                    onClick={copySignUrl}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                    title="Copiar link"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
                {linkCopied && <p className="text-xs text-green-600 mb-4">Link copiado!</p>}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={revokeSignatureLink}
                    className="px-4 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors duration-200 flex items-center gap-2"
                  >
                    <Ban className="w-4 h-4" /> Revogar link
                  </button>
                  <button
                    onClick={() => { setShowSignatureModal(false); setIsSignatureModalExpanded(false); }}
                    className="btn-primary"
                  >
                    Fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {editingTemplateLang && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full overflow-y-auto p-6 transition-all duration-200 ${
            isTemplateModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-4xl max-h-[90vh]'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Languages className="w-5 h-5" /> Revisar modelo — {TEMPLATE_LANG_LABEL[editingTemplateLang]}
              </h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleTemplateModalExpanded}
                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title={isTemplateModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isTemplateModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => { setEditingTemplateLang(null); templateEditRef.current = null; setIsTemplateModalExpanded(false); }}
                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title="Fechar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {editingTemplateLang === 'pt'
                ? 'Revise o texto de cada cláusula. Esta é a versão atualmente usada nos contratos. Não é possível adicionar ou remover cláusulas por aqui.'
                : 'Revise o texto de cada cláusula gerado pela IA. Não é possível adicionar ou remover cláusulas por aqui — para isso, gere novamente com IA.'}
            </p>
            <div className="space-y-4">
              {templateSegments.map((seg, index) =>
                seg.kind === 'title' ? (
                  <div key={index}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{seg.label}</label>
                    <input
                      type="text"
                      value={seg.text}
                      onChange={(e) => updateTemplateSegment(index, e.target.value)}
                      className="w-full px-3 py-2 border rounded-md font-semibold focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : (
                  <div key={index}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{seg.label}</label>
                    <textarea
                      value={seg.text}
                      onChange={(e) => updateTemplateSegment(index, e.target.value)}
                      rows={seg.kind === 'item' ? 2 : 3}
                      className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )
              )}
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={() => { setEditingTemplateLang(null); templateEditRef.current = null; }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveTemplate}
                disabled={savingTemplate}
                className="btn-primary disabled:opacity-50"
              >
                {savingTemplate ? 'Salvando...' : 'Salvar rascunho'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default AdminContrato;
